import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	aprooveDateCard,
	deleteDateCard,
	getDateCardsForModeration,
	getDeleteReasons,
} from "../../api/apiServiceFetch";
import { utcToLocalDateTime } from "../../components/helper";
import "./DateCards.scss";

// Подписи того, что в БД — число (DateCardTimeSlots / DateCardPayerModes /
// DateCardStatuses). Повторяем здесь, потому что backend возвращает только
// id-шники, а модератору на них смотреть смысла нет.
const TIME_SLOTS = {
	1: { label: "Утро", range: "06:00–12:00" },
	2: { label: "День", range: "12:00–18:00" },
	3: { label: "Вечер", range: "18:00–00:00" },
};

const PAYER_MODES = {
	0: "За мой счёт",
	1: "Каждый за себя",
	2: "Платит девушка",
};

const STATUSES = {
	0: { label: "Активна", tone: "ok" },
	1: { label: "Снята автором", tone: "warn" },
	2: { label: "Время прошло", tone: "mute" },
};

// Пере-проверка: автор правил карточку после первого одобрения, и она
// вернулась в очередь. Запоминаем это, чтобы модератор видел «не свежая
// заявка, а изменение» — текст мог поменяться на что-то новое, место тоже.
const EDIT_RECHECK_THRESHOLD_MS = 60_000;

/**
 * Очередь карточек свиданий.
 *
 * Карточку пишет человек своими словами, поэтому до ленты и карты её смотрит
 * модератор — ровно как анкету и фотографии. Пока карточка не одобрена, её не
 * видно никому, кроме автора. Любая правка возвращает карточку сюда повторно.
 */
const DateCards = () => {
	const [cards, setCards] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [busyId, setBusyId] = useState(null);

	const [rejecting, setRejecting] = useState(null);
	const [reasons, setReasons] = useState([]);
	const [selectedReasonId, setSelectedReasonId] = useState("");
	const [customReason, setCustomReason] = useState("");
	const [silent, setSilent] = useState(true);

	// Медленный прошлый запрос не должен заполнить окно чужими причинами.
	const reasonRequestRef = useRef(0);

	const load = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await getDateCardsForModeration();
			setCards(Array.isArray(response) ? response : (response?.data ?? []));
		} catch (e) {
			console.error("Load date cards error:", e);
			setError("Не удалось загрузить карточки");
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const approve = async (card) => {
		setBusyId(card.id);
		try {
			await aprooveDateCard(card.id);
			setCards((prev) => prev.filter((c) => c.id !== card.id));
		} catch (e) {
			console.error("Aproove date card error:", e);
		} finally {
			setBusyId(null);
		}
	};

	const openReject = async (card) => {
		setRejecting(card);
		setSelectedReasonId("");
		setCustomReason("");
		setSilent(true);
		setReasons([]);

		const requestId = ++reasonRequestRef.current;
		try {
			const list = await getDeleteReasons("datecard");
			if (requestId !== reasonRequestRef.current) return;
			setReasons((list ?? []).filter((r) => r.isActive));
		} catch (e) {
			console.error("Load delete reasons error:", e);
		}
	};

	const confirmReject = async () => {
		if (!rejecting) return;

		const card = rejecting;
		setRejecting(null);
		setBusyId(card.id);

		try {
			// Тихий отказ — осознанный выбор: карточку убираем, человека не
			// тревожим. Причина уходит, только когда её выбрали.
			await deleteDateCard(
				card.id,
				silent ? null : selectedReasonId || null,
				silent ? null : customReason.trim() || null,
			);
			setCards((prev) => prev.filter((c) => c.id !== card.id));
		} catch (e) {
			console.error("Delete date card error:", e);
		} finally {
			setBusyId(null);
		}
	};

	const rows = useMemo(
		() =>
			cards.map((card) => {
				const slot = TIME_SLOTS[card.timeSlot];
				const payer = PAYER_MODES[card.payerMode];
				const status = STATUSES[card.status];
				const created = card.createdAtUtc
					? new Date(card.createdAtUtc).getTime()
					: 0;
				const updated = card.updatedAtUtc
					? new Date(card.updatedAtUtc).getTime()
					: 0;
				const isRecheck =
					created > 0 && updated > 0 && updated - created > EDIT_RECHECK_THRESHOLD_MS;
				return {
					...card,
					slot,
					payer: payer ?? "—",
					status,
					createdLabel: card.createdAtUtc ? utcToLocalDateTime(card.createdAtUtc) : "—",
					updatedLabel: card.updatedAtUtc ? utcToLocalDateTime(card.updatedAtUtc) : null,
					isRecheck,
					hasMap:
						typeof card.latitude === "number" &&
						typeof card.longitude === "number",
					initial: (card.name || "?").trim().slice(0, 1).toUpperCase(),
				};
			}),
		[cards],
	);

	return (
		<div className="dcMod">
			<div className="dcMod__head">
				<div>
					<h2 className="dcMod__title">Карточки свиданий</h2>
					<p className="dcMod__sub">
						{isLoading
							? "Загрузка…"
							: rows.length === 0
								? "Очередь пуста — ничего не ждёт проверки."
								: `На проверке: ${rows.length}`}
					</p>
				</div>
				<button
					type="button"
					className="dcMod__refresh"
					onClick={load}
					disabled={isLoading}
				>
					Обновить
				</button>
			</div>

			{error ? <p className="dcMod__error">{error}</p> : null}

			{!isLoading && rows.length > 0 && (
				<div className="dcMod__list">
					{rows.map((card) => (
						<article
							key={card.id}
							className={`dcMod__card${card.isRecheck ? " dcMod__card--recheck" : ""}`}
						>
							<header className="dcMod__cardHead">
								<div
									className={`dcMod__avatar dcMod__avatar--${card.sex === 2 ? "f" : "m"}`}
									aria-hidden="true"
								>
									{card.initial}
								</div>
								<div className="dcMod__person">
									<div className="dcMod__personTop">
										<span className="dcMod__name">
											{[card.name, card.age].filter(Boolean).join(", ") ||
												"Без имени"}
										</span>
										{card.isRecheck && (
											<span className="dcMod__badge dcMod__badge--recheck">
												Повторная проверка
											</span>
										)}
										{card.status && (
											<span
												className={`dcMod__badge dcMod__badge--${card.status.tone}`}
											>
												{card.status.label}
											</span>
										)}
									</div>
									<span className="dcMod__meta">
										<span>id {card.userId}</span>
										{[card.city, card.country].filter(Boolean).length > 0 && (
											<span>·</span>
										)}
										<span>
											{[card.city, card.country].filter(Boolean).join(", ")}
										</span>
									</span>
								</div>
							</header>

							<blockquote className="dcMod__description">
								{card.description || <em>Без описания</em>}
							</blockquote>

							{(card.placeNote || card.hasMap) && (
								<div className="dcMod__place">
									{card.hasMap ? (
										<a
											className="dcMod__placeMapLink"
											href={`https://yandex.com/maps/?pt=${card.longitude},${card.latitude}&z=16&l=map`}
											target="_blank"
											rel="noreferrer noopener"
										>
											<img
												className="dcMod__placeMap"
												alt=""
												loading="lazy"
												src={`https://static-maps.yandex.ru/1.x/?ll=${card.longitude},${card.latitude}&z=15&l=map&size=240,120&pt=${card.longitude},${card.latitude},pm2rdm`}
											/>
										</a>
									) : (
										<div className="dcMod__placeMap dcMod__placeMap--empty">Без карты</div>
									)}
									<div className="dcMod__placeText">
										<div className="dcMod__placeLabel">Место</div>
										<div className="dcMod__placeValue">
											{card.placeNote || "Автор указал только точку"}
										</div>
										{card.hasMap && (
											<div className="dcMod__placeCoords">
												{card.latitude.toFixed(5)}, {card.longitude.toFixed(5)}
											</div>
										)}
									</div>
								</div>
							)}

							<div className="dcMod__facts">
								<div className="dcMod__fact">
									<span className="dcMod__factLabel">Время</span>
									<span className="dcMod__factValue">
										{card.slot ? (
											<>
												<strong>{card.slot.label}</strong>
												<span className="dcMod__factHint">{card.slot.range}</span>
											</>
										) : (
											"Не указано"
										)}
									</span>
								</div>
								<div className="dcMod__fact">
									<span className="dcMod__factLabel">Оплата</span>
									<span className="dcMod__factValue">{card.payer}</span>
								</div>
								<div className="dcMod__fact">
									<span className="dcMod__factLabel">Создана</span>
									<span className="dcMod__factValue">{card.createdLabel}</span>
								</div>
								{card.isRecheck && (
									<div className="dcMod__fact">
										<span className="dcMod__factLabel">Отредактирована</span>
										<span className="dcMod__factValue">{card.updatedLabel}</span>
									</div>
								)}
							</div>

							<div className="dcMod__actions">
								<button
									type="button"
									className="dcMod__btn dcMod__btn--approve"
									onClick={() => approve(card)}
									disabled={busyId === card.id}
								>
									Одобрить
								</button>
								<button
									type="button"
									className="dcMod__btn dcMod__btn--reject"
									onClick={() => openReject(card)}
									disabled={busyId === card.id}
								>
									Отклонить
								</button>
							</div>
						</article>
					))}
				</div>
			)}

			{rejecting ? (
				<div className="filter-modal">
					<div className="filter-modal-content">
						<h3>Причина отказа</h3>

						{reasons.length > 0 ? (
							<select
								className="filter-input"
								value={selectedReasonId}
								onChange={(e) => {
									setSelectedReasonId(e.target.value);
									if (e.target.value) setSilent(false);
								}}
							>
								<option value="">Выберите сообщение</option>
								{reasons.map((r) => (
									<option key={r.id} value={r.id}>
										{r.textRu.length > 60 ? `${r.textRu.slice(0, 60)}…` : r.textRu}
									</option>
								))}
							</select>
						) : (
							<p>Нет доступных сообщений — отказ без уведомления.</p>
						)}

						<p>Своё сообщение</p>
						<textarea
							className="textArea"
							value={customReason}
							onChange={(e) => {
								setCustomReason(e.target.value);
								if (e.target.value.trim()) setSilent(false);
							}}
						/>

						<label className="dcMod__silent">
							<input
								type="checkbox"
								checked={silent}
								onChange={(e) => setSilent(e.target.checked)}
							/>
							Отклонить тихо — не отправлять сообщение автору
						</label>

						<button type="button" onClick={confirmReject}>
							Отклонить карточку
						</button>
						<button type="button" onClick={() => setRejecting(null)}>
							Отмена
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default DateCards;
